import {
  Alert,
  Avatar,
  Button,
  Divider,
  Form,
  Input,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Workspace } from '@core/entities/Workspace';
import type { WorkspaceMember } from '@core/entities/WorkspaceMember';
import type { Invitation } from '@core/entities/Invitation';
import {
  useWorkspaceMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from '@presentation/hooks/useWorkspaceMembers';
import {
  useInviteMember,
  useRevokeInvitation,
  useWorkspaceInvitations,
} from '@presentation/hooks/useInvitations';
import { useRoles } from '@presentation/hooks/useRoles';
import { useAppMessage } from '@presentation/hooks/useAppMessage';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors, snakeToCamelErrorKeys } from '@shared/utils/formErrors';

interface InviteFormValues {
  email: string;
  roleId: string;
}

export const MembersTab = ({ workspace }: { workspace: Workspace }) => {
  const { data: members, isLoading: membersLoading } = useWorkspaceMembers(workspace.id);
  const { data: invitations } = useWorkspaceInvitations(
    workspace.abilities.manageMembers ? workspace.id : undefined,
  );
  const { data: roles } = useRoles();
  const updateRole = useUpdateMemberRole(workspace.id);
  const removeMember = useRemoveMember(workspace.id);
  const inviteMember = useInviteMember(workspace.id);
  const revokeInvitation = useRevokeInvitation(workspace.id);
  const [form] = Form.useForm<InviteFormValues>();
  const message = useAppMessage();

  const handleInvite = (values: InviteFormValues) => {
    inviteMember.mutate(values, {
      onSuccess: () => {
        form.resetFields();
        message.success('Invitation sent.');
      },
      onError: (error) => {
        if (error instanceof ValidationError) {
          // The backend's `role_uuid` field doesn't snake->camel to this
          // form's `roleId` field name, so remap it explicitly rather than
          // relying on the generic snakeToCamelErrorKeys conversion.
          const { role_uuid: roleUuidErrors, ...rest } = error.errors;
          setAntdFormErrors(form, {
            ...snakeToCamelErrorKeys(rest),
            ...(roleUuidErrors ? { roleId: roleUuidErrors } : {}),
          });
        }
      },
    });
  };

  const generalInviteError =
    inviteMember.error && !(inviteMember.error instanceof ValidationError)
      ? inviteMember.error.message
      : null;

  const memberColumns: ColumnsType<WorkspaceMember> = [
    {
      title: 'Member',
      key: 'member',
      render: (_, member) => (
        <div className="flex items-center gap-3">
          <Avatar src={member.user.avatarUrl} icon={<UserOutlined />} />
          <div>
            <div>
              {member.user.name}
              {member.isOwner && (
                <Tag color="blue" className="ml-2">
                  Owner
                </Tag>
              )}
            </div>
            <Typography.Text type="secondary" className="text-xs">
              {member.user.email}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 200,
      render: (_, member) =>
        workspace.abilities.manageMembers && !member.isOwner ? (
          <Select
            size="small"
            value={member.role.id}
            className="w-32"
            onChange={(roleId) =>
              updateRole.mutate(
                { memberId: member.id, roleId },
                { onSuccess: () => message.success('Role updated.') },
              )
            }
            options={roles?.map((role) => ({ value: role.id, label: role.name }))}
          />
        ) : (
          <Typography.Text type="secondary" className="capitalize">
            {member.role.name}
          </Typography.Text>
        ),
    },
    ...(workspace.abilities.manageMembers
      ? [
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, member: WorkspaceMember) =>
              !member.isOwner && (
                <Popconfirm
                  title="Remove this member?"
                  onConfirm={() =>
                    removeMember.mutate(member.id, {
                      onSuccess: () => message.success('Member removed.'),
                    })
                  }
                >
                  <Button type="text" danger size="small">
                    Remove
                  </Button>
                </Popconfirm>
              ),
          },
        ]
      : []),
  ];

  const invitationColumns: ColumnsType<Invitation> = [
    { title: 'Email', dataIndex: 'email' },
    { title: 'Role', key: 'role', render: (_, invitation) => invitation.role.name },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, invitation) => (
        <Button
          type="text"
          danger
          size="small"
          onClick={() =>
            revokeInvitation.mutate(invitation.id, {
              onSuccess: () => message.success('Invitation revoked.'),
            })
          }
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <div className="max-w-2xl space-y-10">
      <section>
        <Typography.Title level={5}>Members</Typography.Title>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          loading={membersLoading}
          columns={memberColumns}
          dataSource={members}
        />
      </section>

      {workspace.abilities.manageMembers && (
        <>
          <section>
            <Typography.Title level={5}>Pending invitations</Typography.Title>
            {invitations && invitations.length === 0 ? (
              <Typography.Text type="secondary">No pending invitations.</Typography.Text>
            ) : (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={invitationColumns}
                dataSource={invitations}
              />
            )}
          </section>

          <Divider />

          <Form form={form} layout="vertical" className="max-w-sm" onFinish={handleInvite}>
            <Typography.Title level={5}>Invite a member</Typography.Title>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: 'Email is required' }, { type: 'email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="roleId"
              label="Role"
              rules={[{ required: true, message: 'Role is required' }]}
            >
              <Select
                placeholder="Select a role…"
                options={roles?.map((role) => ({ value: role.id, label: role.name }))}
              />
            </Form.Item>
            {generalInviteError && (
              <Alert type="error" showIcon title={generalInviteError} className="mb-4" />
            )}
            <Button type="primary" htmlType="submit" loading={inviteMember.isPending}>
              Send invitation
            </Button>
          </Form>
        </>
      )}
    </div>
  );
};
